import { PropTypes } from 'prop-types';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { Icon, Icons } from '@ge/components/icon';
import { Loader } from '@ge/components/loader';
import { Text } from '@ge/components/typography';
import { WidgetNames } from '@ge/feat-reporting/models/widgets';
import { globalColors } from '@ge/tokens';
import { typography } from '@ge/tokens/typography';

import { NoDataAvailable } from '../../no-data-available/no-data-available';
import { LostProductionGraphWidget } from '../lost-production-graph-widget/lost-production-graph-widget';
import { ProductionGraphWidget } from '../production-graph-widget/production-graph-widget';

const BarChartContainer = styled.div`
  display: flex;
  flex-direction: row;
  height: 100%;
  box-sizing: border-box;
  padding: 0 8px;
`;

const LoaderContainer = styled.div`
  height: 100%;
  position: relative;
  width: 100%;
`;

const StyledHeader = styled.div`
  border-bottom: 1px solid ${({ theme }) => theme.createReport.widget.headerBorderColor};
  padding-bottom: 8px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
  text-transform: uppercase;
  color: ${({ theme }) => theme.createReport.widget.headerTextColor};
`;

const NoData = styled.div`
  align-items: center;
  display: flex;
  flex-direction: column;
  justify-content: center;
  width: 100%;
  height: 100%;
`;

const NoDataIcon = styled(Icon).attrs(() => ({
  icon: Icons.DATA_NETCOMM,
  size: 14,
  color: globalColors.grey5,
}))`
  margin: 0 5px 2px 0;
`;

const NoDataTitle = styled(Text).attrs(() => ({
  type: typography.textTypes.body,
}))`
  color: ${({ theme }) => theme.createReport.widget.headerLabelColor};
`;

const barWidgets = {
  [WidgetNames.PRODUCTION]: {
    widget: ProductionGraphWidget,
  },
  [WidgetNames.LOST_PRODUCTION_GRAPH]: {
    widget: LostProductionGraphWidget,
  },
};

// Return the correct implementation for the provided widget name.
const getBarWidgetByName = (widgetName, data, id) => {
  if (widgetName in barWidgets) {
    // If the widget exists and we have an empty data set, show no data available.
    if (!data) {
      return <NoDataAvailable />;
    }

    return React.createElement(barWidgets[widgetName].widget, {
      name: widgetName,
      data,
      id,
    });
  }

  // TODO: Remove this when all widgets have been implemented?
  return (
    <NoData>
      <NoDataIcon />
      <NoDataTitle>Not yet implemented</NoDataTitle>
    </NoData>
  );
};

export const BarWidget = ({ data, name, isLoading, isPlaceholder, id }) => {
  const { t } = useTranslation(['reporting.widgets', 'general']);

  const barWidget = useMemo(() => {
    if (isLoading) {
      return (
        <LoaderContainer>
          <Loader />
        </LoaderContainer>
      );
    }

    if (isPlaceholder) {
      return null;
    }

    return getBarWidgetByName(name, data, id, t);
  }, [name, data, isLoading, isPlaceholder, id, t]);

  return (
    <>
      <StyledHeader>
        <h4>{name}</h4>
      </StyledHeader>
      <BarChartContainer>{barWidget}</BarChartContainer>
    </>
  );
};

BarWidget.propTypes = {
  name: PropTypes.string.isRequired,
  data: PropTypes.instanceOf(Object),
  isLoading: PropTypes.bool,
  isPlaceholder: PropTypes.bool,
  id: PropTypes.string,
};

BarWidget.defaultProps = {
  data: {},
  isLoading: false,
  isPlaceholder: false,
};
